import { useCallback } from 'react';
import { SprintAPI } from '../utils/api';
import { getPrimaryTag } from '../components/SprintDetail/hooks/useTagActions';

/**
 * useSprintIcon Hook
 * 
 * Handles sprint icon changes with inherit color resolution.
 * 
 * If the sprint is a nested campaign (has parentSprintId/parentTaskId), also updates
 * the parent task's icon to keep them in sync.
 * 
 * SAVE ORDER: We save the nested sprint FIRST, then the parent sprint.
 * This ensures that if the parent is currently being viewed, the auto-save
 * triggered by SprintDetail won't race with our manual save.
 */
export function useSprintIcon({
  sprints,
  setSprints,
}) {
  // Resolve 'inherit' color from parent task's primary tag
  const resolveInheritColor = useCallback(async (newIcon, sprintMetadata) => {
    if (newIcon?.color !== 'inherit') {
      return newIcon;
    }
    
    console.log('🎨 Icon has inherit color, checking for parent info...');
    
    // First try to get parent info from local sprints array
    let parentSprintId = sprintMetadata?.parentSprintId;
    let parentTaskId = sprintMetadata?.parentTaskId;
    
    // If not in local state, try to fetch the sprint to get parent info
    if (!parentSprintId || !parentTaskId) {
      console.log('🎨 Parent info not in local state, fetching from API...');
      try {
        const currentSprint = await SprintAPI.get(sprintMetadata?.id);
        parentSprintId = currentSprint?.parentSprintId;
        parentTaskId = currentSprint?.parentTaskId;
        console.log('🎨 Got parent info from API:', { parentSprintId, parentTaskId });
      } catch (error) {
        console.error('Failed to fetch sprint for parent info:', error);
      }
    }
    
    // If we have parent info, resolve the inherit color
    if (parentSprintId && parentTaskId) {
      console.log('🎨 Resolving inherit color from parent task...');
      try {
        const parentSprint = await SprintAPI.get(parentSprintId);
        if (parentSprint?.tasks) {
          const parentTask = parentSprint.tasks.find(t => t.id === parentTaskId);
          if (parentTask) {
            const primaryTagName = getPrimaryTag(parentTask);
            const categories = parentSprint.categories || [];
            const primaryTag = primaryTagName ? categories.find(c => c.name === primaryTagName) : null;
            const resolvedColor = primaryTag?.color || '#9ca3af';
            
            console.log('🎨 Resolved inherit color:', { primaryTagName, resolvedColor });
            return { 
              ...newIcon, 
              color: resolvedColor,
              _inheritedFromTag: primaryTagName, // Track which tag it inherited from
            };
          } else {
            console.log('🎨 Parent task not found, using default gray');
          }
        } else {
          console.log('🎨 Parent sprint has no tasks, using default gray');
        }
      } catch (error) {
        console.error('Failed to resolve inherit color:', error);
      }
    } else {
      // No parent info - this is a top-level campaign, use default gray
      console.log('🎨 No parent info (top-level campaign), using default gray');
    }
    
    return { ...newIcon, color: '#9ca3af' };
  }, []);

  // Change a sprint's icon (from sidebar or header)
  // IMPORTANT: Must fetch full sprint data before updating to avoid overwriting tasks!
  const handleSprintIconChange = useCallback(async (sprintId, newIcon) => {
    console.log('🎨 handleSprintIconChange called:', { sprintId, newIcon });
    console.log('🎨 newIcon?.color:', newIcon?.color);
    console.log('🎨 Is color inherit?:', newIcon?.color === 'inherit');
    
    // Get sprint metadata to check if it's nested
    const sprintMetadata = sprints.find(s => s.id === sprintId);
    console.log('🎨 Sprint metadata:', { 
      sprintId, 
      name: sprintMetadata?.name,
      parentSprintId: sprintMetadata?.parentSprintId, 
      parentTaskId: sprintMetadata?.parentTaskId,
      hasParentSprintId: !!sprintMetadata?.parentSprintId,
      hasParentTaskId: !!sprintMetadata?.parentTaskId,
    });
    console.log('🎨 Full sprint metadata object:', sprintMetadata);
    
    // Resolve 'inherit' color if needed
    const resolvedIcon = await resolveInheritColor(newIcon, { ...sprintMetadata, id: sprintId });
    
    // Update local state immediately (optimistic update for the sprint itself)
    setSprints(prev => prev.map(s => 
      s.id === sprintId ? { ...s, icon: resolvedIcon } : s
    ));
    
    // Dispatch event so SprintDetail can update its local state if this sprint is being viewed
    // This enables optimistic updates in the view headers (Flow, Kanban)
    window.dispatchEvent(new CustomEvent('sprintIconChanged', {
      detail: { sprintId, newIcon: resolvedIcon }
    }));
    
    // STEP 1: Save the nested sprint's icon to API FIRST
    // This must complete before we update the parent, to avoid race conditions
    try {
      const fullSprint = await SprintAPI.get(sprintId);
      if (fullSprint) {
        await SprintAPI.update(sprintId, { ...fullSprint, icon: resolvedIcon });
        console.log('✅ Sprint icon saved to API:', sprintId);
      } else {
        console.error('Failed to fetch full sprint for icon change:', sprintId);
        return; // Don't proceed if we couldn't save the sprint
      }
    } catch (error) {
      console.error('Failed to save sprint icon:', error);
      return; // Don't proceed if save failed
    }
    
    // STEP 2: If this is a nested campaign, update the parent task's icon
    // We do this AFTER saving the nested sprint to avoid race conditions
    if (sprintMetadata?.parentSprintId && sprintMetadata?.parentTaskId) {
      console.log('🎨 Updating parent task icon...');
      
      // Update local state for parent
      setSprints(prev => prev.map(s => {
        if (s.id === sprintMetadata.parentSprintId) {
          return {
            ...s,
            tasks: (s.tasks || []).map(t => 
              t.id === sprintMetadata.parentTaskId 
                ? { ...t, icon: resolvedIcon, updatedAt: new Date().toISOString() }
                : t
            ),
          };
        }
        return s;
      }));
      
      // Dispatch event so SprintDetail can update its local state if parent is being viewed
      window.dispatchEvent(new CustomEvent('taskIconChanged', {
        detail: {
          parentSprintId: sprintMetadata.parentSprintId,
          taskId: sprintMetadata.parentTaskId,
          newIcon: resolvedIcon,
        }
      }));
      
      // Save parent sprint to API (fetch fresh data first)
      try {
        const freshParent = await SprintAPI.get(sprintMetadata.parentSprintId);
        if (freshParent && freshParent.tasks) {
          const updatedParent = {
            ...freshParent,
            tasks: freshParent.tasks.map(t =>
              t.id === sprintMetadata.parentTaskId
                ? { ...t, icon: resolvedIcon, updatedAt: new Date().toISOString() }
                : t
            ),
          };
          await SprintAPI.update(sprintMetadata.parentSprintId, updatedParent);
          console.log('✅ Parent task icon synced with nested campaign');
        }
      } catch (error) {
        console.error('Failed to sync parent task icon:', error);
      }
    }
  }, [sprints, setSprints, resolveInheritColor]);

  return {
    handleSprintIconChange,
  };
}

export default useSprintIcon;

